import { createElement } from "react";
import { toast as sonnerToast } from "sonner";
import { resolveMessage } from "./resolve";
import { TOAST_DURATION } from "./config";
import { ToastCard } from "./ToastCard";
import type {
  ToastId,
  ToastMessage,
  ToastPromiseMessages,
  ToastSeverity,
} from "./types";

/** aria-label for the X close button, resolved once per toast (French or English via i18n). */
function dismissLabel(): string {
  return resolveMessage({ key: "common:actions.dismiss" });
}

/**
 * Render one severity toast as the custom `ToastCard` (the Figma "Notification" design):
 * featured icon + title + description + X close. Sonner owns positioning, stacking, animation,
 * and the live-region semantics; the card owns the visuals. Title AND description are always
 * required — every toast carries both lines (see forms.md).
 */
function show(
  severity: ToastSeverity,
  title: ToastMessage,
  description: ToastMessage
): ToastId {
  const titleText = resolveMessage(title);
  const descriptionText = resolveMessage(description);
  const label = dismissLabel();

  return sonnerToast.custom(
    (id) =>
      createElement(ToastCard, {
        severity,
        title: titleText,
        description: descriptionText,
        onDismiss: () => sonnerToast.dismiss(id),
        dismissLabel: label,
      }),
    { duration: TOAST_DURATION[severity] }
  );
}

/**
 * The app's toast API. Everything outside core/toast calls this — never `sonner` directly.
 * Each method takes an i18n {@link ToastMessage} title AND description (key + optional vars);
 * `resolveMessage` turns them into text. Both are required by design.
 */
export const toast = {
  success(title: ToastMessage, description: ToastMessage): ToastId {
    return show("success", title, description);
  },
  error(title: ToastMessage, description: ToastMessage): ToastId {
    return show("error", title, description);
  },
  info(title: ToastMessage, description: ToastMessage): ToastId {
    return show("info", title, description);
  },
  warning(title: ToastMessage, description: ToastMessage): ToastId {
    return show("warning", title, description);
  },
  /**
   * Drive a toast through a promise's lifecycle (loading → success/error). `success` and
   * `error` may be a static message or a function of the resolved data / rejection reason.
   */
  promise<T>(promise: Promise<T>, messages: ToastPromiseMessages<T>): void {
    sonnerToast.promise(promise, {
      loading: resolveMessage(messages.loading),
      success: (data: T) =>
        resolveMessage(
          typeof messages.success === "function"
            ? messages.success(data)
            : messages.success
        ),
      error: (error: unknown) =>
        resolveMessage(
          typeof messages.error === "function"
            ? messages.error(error)
            : messages.error
        ),
    });
  },
};
